import { Injectable } from '@angular/core';

import moment from 'moment';

import { DonwnloadPendingProcessed, DownloadPending, FormattedTimeRestant, TimeRestant } from 'src/app/models';

@Injectable({
  providedIn: 'root',
})
export class MultipleDonwloadService {
  /**
   * Procesa una lista de descargas pendientes para formatear y actualizar
   * datos relacionados con las fechas de búsqueda y petición.
   *
   * Para cada descarga pendiente, este método:
   * - Convierte `FechaPeticionFormato` de formato UTC a local, actualizando
   *   varios campos relacionados con la fecha.
   * - Divide `fechaBusqueda` en varias partes y determina las fechas de inicio
   *   y fin de búsqueda, manejando diferentes casos basados en la cantidad
   *   de partes.
   * - Establece `fechaBusquedaInicio` y `fechaBusquedaFin` a 'Invalid date'
   *   si el formato de fecha es incorrecto.
   *
   * @param downloads - Lista de descargas pendientes a procesar.
   * @returns Una lista de descargas pendientes procesadas con las fechas formateadas.
   */
  processDownloadsPendings(downloads: DownloadPending[]): DonwnloadPendingProcessed[] {
    let donwnloadsPendingsProcessed = [];
    for (let index = 0; index < downloads.length; index++) {
      donwnloadsPendingsProcessed.push({ ...downloads[index] });
      const element = donwnloadsPendingsProcessed[index];
      const fechasinutc = moment.utc(element['FechaPeticionFormato'], 'DD/MM/YYYY HH:mm:ss').local().format('DD/MM/YYYY HH:mm:ss');
      element['fechanormal'] = element['FechaPeticionFormato'];
      element['fechamomento'] = moment.utc(element['FechaPeticionFormato'], 'DD/MM/YYYY HH:mm:ss').toISOString();
      element['fechautc'] = moment.utc(element['FechaPeticionFormato'], 'DD/MM/YYYY HH:mm:ss').toISOString();
      element['FechaPeticionFormato'] = fechasinutc;

      const s = element['fechaBusqueda'].split(' ');
      if (s.length == 1) {
        const fechastring = s.toString().split('---');
        const inicio = moment(fechastring[0]).local().format('YYYY/MM/DD HH:mm:ss');
        const fin = moment(fechastring[1]).local().format('YYYY/MM/DD HH:mm:ss');
        element['fechaBusquedaInicio'] = inicio;
        element['fechaBusquedaFin'] = fin;
      }

      if (s.length == 2) {
        const inicio = moment(s[0]).local().format('YYYY/MM/DD HH:mm:ss');
        const fin = moment(s[1]).local().format('YYYY/MM/DD HH:mm:ss');
        element['fechaBusquedaInicio'] = inicio;
        element['fechaBusquedaFin'] = fin;
      }

      if (s.length == 3) {
        const inicio = moment(s[0]).local().format('YYYY/MM/DD HH:mm:ss');
        const fin = moment(s[2]).local().format('YYYY/MM/DD HH:mm:ss');
        element['fechaBusquedaInicio'] = inicio;
        element['fechaBusquedaFin'] = fin;
      }

      if (s.length == 4) {
        const inicio = moment(s[0] + ' ' + s[1])
          .local()
          .format('YYYY/MM/DD HH:mm:ss');
        const fin = moment(s[2] + ' ' + s[3])
          .local()
          .format('YYYY/MM/DD HH:mm:ss');
        element['fechaBusquedaInicio'] = inicio;
        element['fechaBusquedaFin'] = fin;
        if (element['fechaBusquedaInicio'] == 'Invalid date') {
          const inicio = moment(s[0].replace(/^(\d{2})-(\d{2})-(\d{4})$/g, '$3/$2/$1') + ' ' + s[1])
            .local()
            .format('YYYY/MM/DD HH:mm:ss');
          const fin = moment(s[2].replace(/^(\d{2})-(\d{2})-(\d{4})$/g, '$3/$2/$1') + ' ' + s[3])
            .local()
            .format('YYYY/MM/DD HH:mm:ss');
          element['fechaBusquedaInicio'] = inicio;
          element['fechaBusquedaFin'] = fin;
        }
        if (element['fechaBusquedaInicio'] == 'Invalid date') {
          element['fechaBusquedaInicio'] = 'Invalid date';
          element['fechaBusquedaFin'] = '';
        }
      }

      if (s.length == 5) {
        const inicio = moment(s[0].replace(/^(\d{2})-(\d{2})-(\d{4})$/g, '$3/$2/$1') + ' ' + s[1])
          .local()
          .format('YYYY/MM/DD HH:mm:ss');
        const fin = moment(s[3].replace(/^(\d{2})-(\d{2})-(\d{4})$/g, '$3/$2/$1') + ' ' + s[4])
          .local()
          .format('YYYY/MM/DD HH:mm:ss');
        element['fechaBusquedaInicio'] = inicio;
        element['fechaBusquedaFin'] = fin;

        if (element['fechaBusquedaInicio'] == 'Invalid date') {
          element['fechaBusquedaInicio'] = 'Invalid date';
          element['fechaBusquedaFin'] = '';
        }
      }

      if (s.length > 5) {
        element['fechaBusquedaInicio'] = 'Invalid date';
        element['fechaBusquedaFin'] = '';
      }
    }

    return donwnloadsPendingsProcessed;
  }

  /**
   * Verifica si se tiene permiso de lectura y escritura sobre una carpeta.
   * Si no se tiene permiso, solicita el permiso al usuario.
   * @param directoryHandle Carpeta a verificar.
   * @returns True si se tiene permiso, false en caso contrario.
   */
  async verifyPermissionFileSystemDirectoryHandle(directoryHandle: any) {
    const permissionOpts = { mode: 'readwrite' };
    if ((await directoryHandle.queryPermission(permissionOpts)) !== 'granted') {
      if ((await (directoryHandle as any).requestPermission(permissionOpts)) !== 'granted') {
        return false;
      } else {
        return true;
      }
    }

    return true;
  }

  /**
   * Sanitiza un nombre de archivo reemplazando todos los caracteres no alfanumericos,
   * excepto el guion bajo, el guion y el punto, por un guion bajo.
   * @param name nombre del archivo a Sanitizar
   * @returns nombre del archivo sanitizado
   */
  sanitizeFileName(name: string): string {
    return name.replace(/[^a-z0-9_\-\.]/gi, '_');
  }

  /**
   * Convierte un numero de segundos en un objeto FormattedTimeRestant con
   * la cantidad de tiempo restante en dias, horas, minutos o segundos.
   * @param secondsRemaining numero de segundos restantes
   * @returns objeto con la cantidad de tiempo restante y su unidad
   */
  formattedTimeRestant(secondsRemaining: number): FormattedTimeRestant {
    const result: FormattedTimeRestant = { timeRestant: null, unit: null };

    if (secondsRemaining < TimeRestant.seconds) {
      result.timeRestant = Math.ceil(secondsRemaining);
      result.unit = 'segundos';
    } else if (secondsRemaining >= TimeRestant.seconds && secondsRemaining < TimeRestant.minutes) {
      result.timeRestant = Math.ceil(secondsRemaining / TimeRestant.seconds);
      result.unit = 'minutos';
    } else if (secondsRemaining >= TimeRestant.minutes && secondsRemaining < TimeRestant.hours) {
      result.timeRestant = Math.ceil(secondsRemaining / TimeRestant.minutes);
      result.unit = 'horas';
    } else {
      result.timeRestant = Math.ceil(secondsRemaining / TimeRestant.hours);
      result.unit = 'dias';
    }

    return result;
  }
}
